import { useEffect, useState } from 'react';

interface Options {
  /** What the visitor actually typed. */
  searchTerm?: string;
  /**
   * Term derived from the best-matching PRODUCT (its collection/range, else its category) — see
   * CustomStats. `undefined` while the products query is still in flight, `''` when there is no match.
   */
  derivedTerm?: string;
  /**
   * Content results the CURRENT term returned. `undefined` until the content query has resolved — the
   * distinction matters, since 0 is the trigger to fall back and "not known yet" must not be.
   */
  contentHits?: number;
}

/**
 * Which term the content-suggestions index should search.
 *
 * The visitor's own words win: the raw search term is tried FIRST, and the product-derived term is used
 * only if the raw term found no content at all. (It used to be the other way round — the derived term
 * took precedence as soon as it existed, so a search for "car" fetched content for "Care Toilets" even
 * when there was perfectly good content for "car".)
 *
 * The switch is latched per search term. Without that latch the fallback oscillates: raw term returns 0
 * → switch to derived → derived returns some hits → `contentHits > 0` → switch back to raw → 0 again.
 */
export const useContentSearchTerm = ({ searchTerm, derivedTerm, contentHits }: Options): string => {
  const [useDerived, setUseDerived] = useState(false);

  // A new search starts over on the raw term.
  useEffect(() => {
    setUseDerived(false);
  }, [searchTerm]);

  useEffect(() => {
    if (useDerived) return; // latched — never switch back for this term
    if (contentHits === undefined) return; // the raw term's result is not in yet
    if (contentHits > 0) return; // the raw term found content; nothing to fall back to
    if (!derivedTerm) return; // no product-derived term available (or products matched nothing)
    setUseDerived(true);
  }, [contentHits, derivedTerm, useDerived]);

  return useDerived && derivedTerm ? derivedTerm : (searchTerm ?? '');
};

export default useContentSearchTerm;
